import { FC, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import lodash from 'lodash';
import { useSnackbar } from 'notistack';
import { Button, Tooltip } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import ScrollItem from 'src/components/Items/ScrollItem';
import ShareModal from 'src/components/Items/ShareModal';
import VanHost from './Host';
import VanPhotos from './Photos';
import VanExtras from './Extras';
import SingleVanModal from '../Modal/SingleVanModal';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import useIsMobile from 'src/hooks/useIsMobile';
import { useActions } from 'src/hooks/useActions';
import { VanFilterErrors } from 'src/store/reducers/van/types';
import { cutText, scrollToTop } from 'src/common/utils/others';
import showFilterErrors from 'src/common/utils/showFilterErrors';
import { dineroFormat } from 'src/common/utils/dataFormat';
import createVanURL from 'src/common/utils/createVanURL';
import { IVanFetch } from 'src/models/IVan';
import Icons from 'src/common/icons';
import './styles.scss';
import './media.scss';

interface IProps {
  van: IVanFetch;
  isFilterValidate: boolean;
  errors: VanFilterErrors;
  container?: Element;
}

const VanItem: FC<IProps> = ({ van, isFilterValidate, errors, container }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { setupOrder, setCheckFilter } = useActions();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const {
    location: { locationList },
    subLocation: { sublocationList }
  } = useTypedSelector(state => state);
  const selectedLocation = useTypedSelector(state => state.van.filters.location);

  const {
    id,
    description,
    photos,
    mark,
    price,
    currency,
    make,
    type,
    year,
    tags,
    host,
    features,
    location,
    near_van
  } = van;

  const vanURL = createVanURL({ id, location, make, type, year });
  const near = near_van.find(n => n.location === selectedLocation);

  const [subLocationNames, setSubLocationNames] = useState<string[]>([]);
  const [mainLocation, setMainLocation] = useState<string>();
  const [openVanModal, setOpenVanModal] = useState(false);
  const [isOpenShareInModal, setIsOpenShareInModal] = useState(false);
  const [openShare, setOpenShare] = useState(false);

  useEffect(() => {
    const locationTags = tags.filter(tag => tag.category === 'sublocation').map(i => i.name);
    const subNames = locationList
      .find(location => location.name === selectedLocation)
      ?.sub_location.map(sub => sub.name);
    if (subNames) {
      const intersection = lodash.intersection(locationTags, subNames);
      setSubLocationNames(intersection);
    }
    // check if van location is sub.
    const sub = sublocationList.find(l => l.name === location);
    if (sub) setMainLocation(sub.main_location_name);
    else setMainLocation(location);
  }, [selectedLocation]);

  const handleOpenVanModalWithShare = () => {
    setOpenVanModal(true);
    setIsOpenShareInModal(true);
  };

  const handleCloseVanModal = () => {
    setOpenVanModal(false);
    setIsOpenShareInModal(false);
  };

  const handleOrder = () => {
    if (isFilterValidate) {
      setupOrder(van);
      navigate('/order');
    } else {
      setCheckFilter();
      !isMobile && showFilterErrors(enqueueSnackbar, errors);
      if (container) {
        container!.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else scrollToTop();
    }
  };

  return (
    <div className='van__card'>
      <VanPhotos photos={photos} />
      <div>
        <div className='van__info'>
          {isMobile ? (
            <Link to={vanURL} target='_blank' className='van__info__name'>
              {mark}
            </Link>
          ) : (
            <div className='van__info__name' onClick={() => setOpenVanModal(true)}>
              {mark}
            </div>
          )}
          <Tooltip title='Share van link'>
            <IosShareIcon
              onClick={isMobile ? () => setOpenShare(true) : handleOpenVanModalWithShare}
            />
          </Tooltip>
          <div className='van__info__price'>
            {dineroFormat(price, currency)}
            <div className='van__info__price__description'>average per day</div>
          </div>
        </div>
        {subLocationNames.length ? (
          <ScrollItem array={subLocationNames} id={id} className='sub-locations' />
        ) : (
          <></>
        )}
        <div className='van__info__description'>
          {cutText(description, 220)}
          {isMobile ? (
            <Link to={vanURL} target='_blank' className='van__info__description_more'>
              read more
            </Link>
          ) : (
            <div className='van__info__description_more' onClick={() => setOpenVanModal(true)}>
              read more
            </div>
          )}
        </div>
        <div className='van__capacity'>
          <div className='van__capacity__title'>Features:</div>
          <ScrollItem id={id} van={van} array={features} className='van__capacity__container' />
        </div>
        <div className='van__order'>
          <div
            className={`
            van__order__container ${van.type === 'passenger' && 'van__order__delivery-container'}
          `}
          >
            <VanHost host={host} />
            <VanExtras type={van.type} />
          </div>
          <div className='van__order__block'>
            <Button
              className='van__order__btn'
              onClick={handleOrder}
              children='Book / Quote'
              variant='contained'
            />
          </div>
        </div>
        {selectedLocation && selectedLocation !== mainLocation && near ? (
          <div className='van__info__near-van'>
            <div className='van__info__near-van__price'>
              <Icons.Label classes='van__info__near-van__price__icon' />
              <span>
                It’s in {mainLocation}. We deliver for {dineroFormat(near.price, near.currency)}
              </span>
            </div>
          </div>
        ) : (
          // {selectedLocation && selectedLocation !== mainLocation ? (
          //   <div className='van__info__near-van'>
          //     {near ? (
          //       <div className='van__info__near-van__price'>
          //         <Icons.Label classes='van__info__near-van__price__icon' />
          //         <span>
          //           It’s in {mainLocation}. We deliver for {dineroFormat(near.price, near.currency)}
          //         </span>
          //       </div>
          //     ) : (
          //       <div className='van__info__near-van__price'>
          //         <Icons.Warning />
          //         <span>Delivery from {mainLocation} possible - get a quote to confirm.</span>
          //       </div>
          //     )}
          //   </div>
          <></>
        )}
      </div>
      <SingleVanModal
        open={openVanModal}
        handleClose={handleCloseVanModal}
        van={van}
        isOpenShare={isOpenShareInModal}
      />
      <ShareModal
        url={`https://van.sprinter-rentals.com${vanURL}`}
        open={openShare}
        onClose={() => setOpenShare(false)}
        className='down'
      />
    </div>
  );
};
export default VanItem;
